import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { useRecoilState, useResetRecoilState } from "recoil";
import { useMutation, useQuery } from "@apollo/react-hooks";
import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import humanMaleFemale from "@iconify/icons-mdi/human-male-female";
import {
  useMyHrContractsByEmployee,
  useMyHrSpousesData as useMyHrSpousesDataDI,
} from "my-hr/hooks";
import MyTaxApplicationCardInfoItem from "my-tax/components/application-card-info-item/application-card-info-item";
import ApplicationCollapseHeader from "my-tax/components/application-collapse-header";
import MyTaxApplicationHeader from "my-tax/components/application-header/application-header";
import DependentsCollapse from "my-tax/components/dependents-collapse";
import MyTaxCombinedTaxCreditApplicationPrint from "my-tax/components/combined-tax-credit-application-print/combined-tax-credit-application-print";
import MyTaxSpouseCollapse from "my-tax/components/spouse-collapse/spouse-collapse";
import TaxCreditApplicationButtonBars from "my-tax/components/tax-credit-application-buttonbars/tax-credit-application-buttonbars";
import MyTaxCreditApplicationCheckboxLine from "my-tax/components/tax-credit-application-checkbox-line/tax-credit-application-checkbox-line";
import MyTaxCreditApplicationErrorBox from "my-tax/components/tax-credit-application-error-box/tax-credit-application-error-box";
import { NOT_PROVIDED } from "my-tax/constants";
import { MY_TAX_GET_COMBINED_DECLARATION } from "my-tax/graphql/queries/get-tax-declaration-combined";
import { MY_TAX_CREATE_COMBINED_DECLARATION } from "my-tax/graphql/mutations/create-tax-declaration-combined";
import { aloneOrWithSpouseState } from "my-tax/states/alone-or-with-spouse-state";
import { dependentDataState } from "my-tax/states/dependent-data-state";
import { childrenCountState } from "my-tax/states/children-count-state";
import { taxDeclarationCombined } from "my-tax/states/tax-declaration-combined";
import { OcCard, OcConfirmationDialog, OcDownload } from "oc/components";
import {
  useOcDependentsData as useDependentsDataDI,
  useOcCurrentUserState,
  useOcSnackbar,
} from "oc/hooks";

type UseFormDataProps = {
  _id: number | string | undefined;
  setSelected: (params: any) => void;
};

const useFormData = ({ _id, setSelected }: UseFormDataProps) => {
  const { t } = useTranslation();

  const [saveErrors, setSaveErrors] = React.useState<string[]>([]);
  const [disabledButton, setDisabledButton] = React.useState(false);
  const [states, setStates] = useRecoilState(taxDeclarationCombined);
  const resetStates = useResetRecoilState(taxDeclarationCombined);

  const { data, loading, error } = useQuery(MY_TAX_GET_COMBINED_DECLARATION, {
    fetchPolicy: "cache-and-network",
    skip: !_id,
    variables: {
      _id: parseInt(_id?.toString() ?? "0"),
    },
  });

  const result = data?.taxDeclarationCombined;

  // Sync query result to Recoil state (replaces deprecated onCompleted)
  React.useEffect(() => {
    if (!loading && result) {
      const newStates = { ...result };
      newStates.dateOfDeclaration = new Date(result?.dateOfDeclaration);
      delete newStates.__typename;
      setStates(newStates);
    }
  }, [loading, result, setStates]);

  const handleChange = ({ key, value }: { key: string; value: any }) => {
    const newState = { ...states, [key]: value };
    setStates(newState);
  };

  const handleStateChange = (newState: typeof states) => {
    setStates(newState);
  };

  const { setOpenMessage } = useOcSnackbar();
  const [saveDeclaration, { loading: mutationLoading }] = useMutation(
    MY_TAX_CREATE_COMBINED_DECLARATION,
    {
      onCompleted: (data) => {
        const res = data?.createTaxDeclarationCombined;
        setDisabledButton(false);
        if (res?.result) {
          if (res?.messages?.length > 0) {
            const message = res?.messages[0];
            if (
              message === "A korábban beadott nyilatkozata törlésre került!"
            ) {
              setOpenMessage({
                type: "success",
                message: t("MY_TAX_DECLARATION_CREATED_SUCCESSFULLY"),
              });
              setOpenMessage({
                type: "warning",
                message: t("MY_TAX_PREVIOUS_TAX_DECLARATION_DELETED"),
              });
            }
          } else {
            setOpenMessage({
              type: "success",
              message: t("MY_TAX_DECLARATION_CREATED_SUCCESSFULLY"),
            });
          }
          setSaveErrors([]);
          const params = {
            editable: false,
            tableId: res?.tableId,
            year: states?.dateOfDeclaration?.getFullYear().toString(),
            type: "TaxDeclarationCombined",
            state: "requested",
          };
          setSelected(params);
        } else {
          setSaveErrors(res?.messages);
          setDisabledButton(false);
        }
      },
      onError: () => {
        setSaveErrors([t("MY_TAX_DECLARATION_CREATED_FAILED")]);
        setDisabledButton(false);
      },
    },
  );


  const handleSave = () => {
    setDisabledButton(true);
    const params = { ...states };
    if (params?.spouseEmployerTaxNumber) {
      params.spouseEmployerTaxNumber = params?.spouseEmployerTaxNumber?.replace(
        /[-\s]/g,
        "",
      );
    }
    saveDeclaration({ variables: params });
  };

  return {
    data: result,
    loading: loading || mutationLoading,
    error,
    states,
    disabledButton,
    resetStates,
    handleChange,
    handleStateChange,
    handleSave,
    saveErrors,
    setSaveErrors,
  };
};

type MyTaxCombinedTaxCreditApplicationProps = {
  fullName: string;
  taxNumber: string;
  useDependentsData?: typeof useDependentsDataDI;
  useMyHrSpousesData?: typeof useMyHrSpousesDataDI;
  taxApplicationSaveEnabled: boolean;
  handleRemoveRequest?: () => void;
  selected: any;
  setSelected: (params: any) => void;
  isMobile: boolean;
};

const MyTaxCombinedTaxCreditApplication = ({
  fullName,
  taxNumber,
  useDependentsData = useDependentsDataDI,
  useMyHrSpousesData = useMyHrSpousesDataDI,
  taxApplicationSaveEnabled,
  handleRemoveRequest,
  selected,
  setSelected,
  isMobile,
}: MyTaxCombinedTaxCreditApplicationProps) => {
  const editable = selected?.editable;
  const { t } = useTranslation();
  const history = useHistory();
  const theme = useTheme();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const {
    states,
    loading,
    error,
    saveErrors,
    disabledButton,
    setSaveErrors,
    resetStates,
    handleChange,
    handleStateChange,
    handleSave,
  } = useFormData({
    _id: selected?.tableId,
    setSelected,
  });

  const showModifiedButton = states?.state === "E" ? true : false;
  const showRemoveButton =
    states?.state === "K" || selected?.state === "K" ? true : false;

  const dateOfDeclarationString = new Date(
    states?.dateOfDeclaration,
  ).toLocaleDateString();

  const title =
    "Összevont adóelőleg-nyilatkozat a családi kedvezmény és a két, a három, illetve a négy vagy több gyermeket nevelő anyák kedvezményének érvényesítéséről";
  const { data: dependents } = useDependentsData();
  const { data: spouses } = useMyHrSpousesData();

  useEffect(() => {
    if (editable) {
      if (spouses?.length > 0) {
        if (spouses[0].name !== states?.spouseName) {
          handleChange({ key: "spouseName", value: spouses[0].name });
        }
        if (spouses[0].taxNumber !== states?.spouseTaxNumber) {
          handleChange({ key: "spouseTaxNumber", value: spouses[0].taxNumber });
        }
      }
    }
  }, [
    editable,
    spouses,
    states?.spouseName,
    states?.spouseTaxNumber,
    handleChange,
  ]);

  const componentRef = useRef(null);

  const print = useReactToPrint({
    content: () => componentRef.current,
  });

  const handlePrint = () => {
    print();
  };

  const [radioGroupState, setRadioGroupState] = useRecoilState(
    aloneOrWithSpouseState,
  );
  const resetRadioGroupState = useResetRecoilState(aloneOrWithSpouseState);

  const [childrenCountGroupState, setChildrenCountGroupState] = useRecoilState(
    childrenCountState,
  );
  const resetChildrenCountGroupState = useResetRecoilState(childrenCountState);

  const resetDependentDataState = useResetRecoilState(dependentDataState);

  const handleCancel = () => {
    // Only reset states when in edit mode
    if (editable) {
      resetRadioGroupState();
      resetChildrenCountGroupState();
      resetDependentDataState();
      resetStates();
    }
    setSaveErrors([]);
  };

  const handleDependentsChange = (props: {
    dependentSerialNumber: number | string;
    [key: string]: any;
  }) => {
    const index = states?.dependents.findIndex(
      (item: any) => item.dependentSerialNumber === props.dependentSerialNumber,
    );
    const newStates = { ...states };
    const dependents = [...(newStates?.dependents ?? [])];
    dependents[index] = props;
    newStates.dependents = dependents;
    handleStateChange(newStates);
  };

  useEffect(() => {
    if (editable) {
      dependents?.forEach((item: any) => {
        const dateOfBirth = item.birthDate ? new Date(item.birthDate) : null;
        const newItem = {
          dependentSerialNumber: item.itemSerial,
          name: item.name,
          taxNumber: item.taxNumber,
          dependentType: "",
          entitlementTitle: "",
          changeDate: null,
          placeOfBirth: item.birthPlace || "",
          dateOfBirth: dateOfBirth && dateOfBirth.toString() !== "Invalid Date" ? dateOfBirth.toLocaleDateString() : "",
        };
        const isExist = states?.dependents.find(
          (item: any) =>
            item.dependentSerialNumber === newItem.dependentSerialNumber,
        );
        if (!isExist) {
          const newDependents = [...(states?.dependents || []), newItem];
          handleChange({ key: "dependents", value: newDependents });
        }
      });
    }
  }, [editable, dependents, states?.dependents, handleChange]);

  let disabled = true;

  let { contractCode, employeeCode } = useOcCurrentUserState();
  const { data: contractsData } = useMyHrContractsByEmployee({
    employee: employeeCode,
  });

  let selectedContract = contractsData?.find(
    (item: any) => item.code === contractCode,
  );

  const handleRadioGroupChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value === "alone") {
      const newStates = { ...states, applyAlone: true, applyTogether: false };
      handleStateChange(newStates);
    } else {
      const newStates = { ...states, applyAlone: false, applyTogether: true };
      handleStateChange(newStates);
    }
    setRadioGroupState(e.target.value);
  };

  const handleChildrenCountRadioGroupChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value === "twoChildren") {
      const newStates = { ...states, isEligibleTwo: true, isEligibleThree: false, isEligible: false };
      handleStateChange(newStates);
    } else if (e.target.value === "threeChildren") {
      const newStates = { ...states, isEligibleTwo: false, isEligibleThree: true, isEligible: false };
      handleStateChange(newStates);
    } else {
      const newStates = { ...states, isEligibleTwo: false, isEligibleThree: false, isEligible: true };
      handleStateChange(newStates);
    }
    setChildrenCountGroupState(e.target.value);
  };

  useEffect(() => {
    if (!editable && states?.applyTogether && radioGroupState !== "withSpouse") {
      setRadioGroupState("withSpouse");
    } else if (!editable && states?.applyAlone && radioGroupState !== "alone") {
      setRadioGroupState("alone");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editable, states?.applyTogether, states?.applyAlone]);

  useEffect(() => {
    let next = "";
    if (states?.isEligibleTwo) {
      next = "twoChildren";
    } else if (states?.isEligibleThree) {
      next = "threeChildren";
    } else if (states?.isEligible) {
      next = "fourOrMoreChildren";
    }

    if (childrenCountGroupState !== next && next !== "") {
      setChildrenCountGroupState(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    states?.isEligibleTwo,
    states?.isEligibleThree,
    states?.isEligible,
  ]);

  const handleDialogConfirm = () => {
    setShowConfirmDialog(false);
    handleSave();
  };

  const handleDialogCancel = () => {
    setShowConfirmDialog(false);
  };

  const handleScrollToError = () => {
    const errorElement = document.getElementById("errorBox");
    if (errorElement) {
      window.scrollTo({
        top: 300,
        behavior: "smooth",
      });
    }
  };

  useLayoutEffect(() => {
    handleScrollToError();
  }, [saveErrors, history.action]);

  useEffect(() => {
    // Only reset when creating a NEW declaration (editable=true AND no tableId)
    if (editable && !selected?.tableId) {
      resetStates();
    }
    if (history.action === "POP" && editable) {
      return handleCancel();
    }
  }, []); //eslint-disable-line

  return (
    <>
      <Box
        style={{
          marginBottom: "-15px",
        }}
      >
        <Box
          sx={{
            display: "none",
            displayPrint: "block",
            paddingRight: "85px",
          }}
        >
          <MyTaxCombinedTaxCreditApplicationPrint
            ref={componentRef}
            fullName={fullName}
            taxNumber={taxNumber}
            aloneOrWithSpouse={radioGroupState}
            employerName={selectedContract?.company?.name || ""}
            employerTaxNumber={selectedContract?.company?.taxNumber || ""}
            states={states}
            applicationDate={dateOfDeclarationString}
          />
        </Box>
        <MyTaxApplicationHeader
          isMobile={isMobile}
          title={title}
          onCancel={handleCancel}
        />
        <Box height="16px" />
        <Box display="flex" justifyContent="space-around">
          <Box
            style={{
              padding: "7px",
              width: "260px",
              borderRadius: "4px",
            }}
          >
            <OcDownload
              iconType="document"
              label="Tájékoztató"
              actionIconType="url"
              href={"#/my-hr/combined-tax-credit-information"}
              target="_blank"
              isSmall={true}
            />
          </Box>
        </Box>
        <Box height="16px" />
        {saveErrors.length > 0 && (
          <Box id="errorBox">
            <MyTaxCreditApplicationErrorBox error={saveErrors} />
          </Box>
        )}
        <OcCard
          label="I. A nyilatkozatot adó magánszemély"
          loading={loading}
          error={!!error}
        >
          <Box height="36px" />
          <MyTaxApplicationCardInfoItem
            label="Dátum"
            value={dateOfDeclarationString}
            isMobile={isMobile}
          />
          <MyTaxApplicationCardInfoItem
            label="Dolgozó neve"
            value={fullName}
            isMobile={isMobile}
          />
          <MyTaxApplicationCardInfoItem
            label="Adóazonosító jele"
            value={taxNumber}
            isMobile={isMobile}
          />
          <Box height="15px" />
          <Box display="flex" flexDirection="row">
            <Typography variant="body1" color={editable ? "primary" : "info"}>
              1.
            </Typography>
            <Box width="16px" />
            <Typography variant="body1" color={editable ? "primary" : "info"}>
              Nyilatkozom, hogy jogosult vagyok, és igénybe kívánom venni a családi kedvezményt és
            </Typography>
          </Box>
          <FormControl
            component="fieldset"
            sx={{
              marginLeft: "30px",
              alignItems: "flex-start",
              display: "flex",
            }}
          >
            <Box height="8px" />
            <RadioGroup
              value={childrenCountGroupState}
              aria-label="children"
              name="children-count-radio-group"
              onChange={handleChildrenCountRadioGroupChange}
            >
              <FormControlLabel
                value="twoChildren"
                control={
                  <Radio
                    id="radio-twoChildren"
                    name="children-count-radio-group"
                    style={{
                      color: !editable
                        ? theme.palette.gray60.main
                        : theme.palette.darkBase.main,
                    }}
                    disabled={!editable}
                  />
                }
                label={
                  <Typography
                    variant="body1"
                    color={editable ? "primary" : "info"}
                    style={{ alignItems: "center" }}
                  >
                    a két gyermeket nevelő anyák kedvezményét
                  </Typography>
                }
              />
              <Box height="3px" />
              <FormControlLabel
                value="threeChildren"
                control={
                  <Radio
                    id="radio-threeChildren"
                    name="children-count-radio-group"
                    style={{
                      color: !editable
                        ? theme.palette.gray60.main
                        : theme.palette.darkBase.main,
                    }}
                    disabled={!editable}
                  />
                }
                label={
                  <Typography
                    variant="body1"
                    color={editable ? "primary" : "info"}
                    style={{ alignItems: "center" }}
                  >
                    a három gyermeket nevelő anyák kedvezményét
                  </Typography>
                }
              />
              <Box height="3px" />
              <FormControlLabel
                value="fourOrMoreChildren"
                control={
                  <Radio
                    id="radio-fourOrMoreChildren"
                    name="children-count-radio-group"
                    style={{
                      color: !editable
                        ? theme.palette.gray60.main
                        : theme.palette.darkBase.main,
                    }}
                    disabled={!editable}
                  />
                }
                label={
                  <Typography
                    variant="body1"
                    color={editable ? "primary" : "info"}
                    style={{ alignItems: "center" }}
                  >
                    a négy vagy több gyermeket nevelő anyák kedvezményét
                  </Typography>
                }
              />
            </RadioGroup>
          </FormControl>
          <Typography
            variant="body1"
            color={editable ? "primary" : "info"}
            style={{
              alignItems: "center", marginLeft: "30px",
            }}
          >
            és külföldi államban a jövedelmem után azonos vagy hasonló kedvezményeket nem veszek
            (vettem) igénybe.
          </Typography>
          <Box height="36px" />
          <Box display="flex" flexDirection="row">
            <Typography variant="body1" color={editable ? "primary" : "info"}>
              2.
            </Typography>
            <Box width="16px" />
            <Typography variant="body1" color={editable ? "primary" : "info"}>
              Az eltartottak adatai
            </Typography>
          </Box>
          <Box height="36px" />
          {states?.dependents.length !== 0 ? (
            states?.dependents?.map((item: any, index: number) => {
              return (
                <Box key={`dependents-${index}`}>
                  <DependentsCollapse
                    index={index}
                    data={item}
                    id={`dependents-${item.dependentSerialNumber ?? index}`}
                    onChange={handleDependentsChange}
                    isModifiedApplication={false}
                    editable={editable}
                    isMobile={isMobile}
                    variant="combined"
                  />
                  <Box height="36px" />
                </Box>
              );
            })
          ) : (
            <Box>
              <ApplicationCollapseHeader
                dependentName={NOT_PROVIDED}
                dependentTaxNumber=""
                expanded={disabled}
                icon={humanMaleFemale}
                taxNumberLabel=""
                handleExpandClick={() => { }}
                isMobile={isMobile}
              />
              <Box height="36px" />
            </Box>
          )}
          <Box display="flex" flexDirection="row">
            <Typography variant="body1" color={editable ? "primary" : "info"}>
              3.
            </Typography>
            <Box width="16px" />
            <Typography variant="body1" color={editable ? "primary" : "info"}>
              Nyilatkozom, hogy a családi kedvezményt
            </Typography>
          </Box>
          <FormControl
            component="fieldset"
            sx={{
              marginLeft: "30px",
              alignItems: "flex-start",
              display: "flex",
            }}
          >
            <Box height="8px" />
            <RadioGroup
              value={radioGroupState}
              aria-label="gender"
              name="alone-or-spouse-radio-group"
              onChange={handleRadioGroupChange}
            >
              <FormControlLabel
                value="alone"
                control={
                  <Radio
                    id="radio-alone"
                    name="alone-or-spouse-radio-group"
                    style={{
                      color: !editable
                        ? theme.palette.gray60.main
                        : theme.palette.darkBase.main,
                    }}
                    disabled={!editable}
                  />
                }
                label={
                  <Typography
                    variant="body1"
                    color={editable ? "primary" : "info"}
                    style={{ alignItems: "center" }}
                  >
                    egyedül,
                  </Typography>
                }
              />
              <Box height="3px" />
              <FormControlLabel
                value="withSpouse"
                control={
                  <Radio
                    id="radio-withSpouse"
                    name="alone-or-spouse-radio-group"
                    style={{
                      color: !editable
                        ? theme.palette.gray60.main
                        : theme.palette.darkBase.main,
                    }}
                    disabled={!editable}
                  />
                }
                label={
                  <Typography
                    variant="body1"
                    color={editable ? "primary" : "info"}
                    style={{ alignItems: "center" }}
                  >
                    vagy jogosult házastársammal/élettársammal közösen
                    érvényesítem.
                  </Typography>
                }
              />
            </RadioGroup>
          </FormControl>
          <Box height="36px" />
          <MyTaxCreditApplicationCheckboxLine
            id="noApply"
            type="checkbox"
            prefix="4."
            checked={states?.noRequestRelief}
            onChange={(e) =>
              handleChange({
                key: "noRequestRelief",
                value: e.target.checked,
              })
            }
            text="Nyilatkozom, hogy nem kérem a családi járulékkedvezmény havi
                összegének érvényesítését."
            checkboxDisabled={!editable}
            editable={editable}
          />
          <Box height="36px" />
          <Box display="flex">
            <Typography variant="body1" color={editable ? "primary" : "info"}>
              5.
            </Typography>
            <Box width="16px" />
            <Typography variant="body1" color={editable ? "primary" : "info"}>
              A fentiek alapján nyilatkozom, hogy a családi kedvezményt
            </Typography>
          </Box>
          <Box height="8px" />
          <Box paddingLeft="35px">
            <MyTaxCreditApplicationCheckboxLine
              id="amount"
              prefix="a."
              text="forint összegben kívánom igénybe venni."
              textInput={true}
              textInputType="number"
              value={states?.taxReliefAmount}
              label={t("OC_AMOUNT")}
              checked={false}
              onChange={() => { }}
              onChangeInput={(e) =>
                handleChange({
                  key: "taxReliefAmount",
                  value: parseFloat(e.target.value.replace(/[^0-9]/g, "")),
                })
              }
              radioDisabled={states?.taxReliefNumberOfChild > 0 || !editable}
              textInputDisabled={
                states?.taxReliefNumberOfChild > 0 || !editable
              }
              isMobile={isMobile}
              editable={editable}
            />
            <Box height="16px" />
            <MyTaxCreditApplicationCheckboxLine
              id="numberOfApplicationDependent"
              prefix="b."
              text="fő kedvezményezett eltartott után kívánom igénybe venni."
              textInput={true}
              textInputType="number"
              value={
                states?.taxReliefNumberOfChild === 0
                  ? ""
                  : states?.taxReliefNumberOfChild
              }
              label="Fő"
              onChangeInput={(e) =>
                handleChange({
                  key: "taxReliefNumberOfChild",
                  value: parseFloat(e.target.value.replace(/[^0-9]/g, "")),
                })
              }
              radioDisabled={states?.taxReliefAmount > 0 || !editable}
              textInputDisabled={states?.taxReliefAmount > 0 || !editable}
              isMobile={isMobile}
              editable={editable}
              checked={false}
            />
          </Box>
          <Box height="16px" />
          <Box marginLeft="20px">
            <Typography
              variant="body1"
              color="info"
              style={{
                textIndent: isMobile ? "15px" : "60px",
              }}
            >
              Az a. vagy b. sor egyikét töltse ki!
            </Typography>
          </Box>
          <Box height="36px" />
          <Box display="flex" flexDirection="row">
            <Typography variant="body1" color={editable ? "primary" : "info"}>
              6.
            </Typography>
            <Box width="16px" />
            <Typography variant="body1" color={editable ? "primary" : "info"}>
              Felelősségem tudatában kijelentem, hogy a nyilatkozat tartalmát
              házastársam, élettársam megismerte és elfogadta.
            </Typography>
          </Box>
          <Box height="26px" />
        </OcCard>
        <Box height="16px" />
        <OcCard label="II. A családi kedvezményt kérő magánszemély jogosult házastársa/ élettársa">
          <Box height="36px" />
          {spouses.length !== 0 ? (
            <MyTaxSpouseCollapse
              editable={editable}
              states={states}
              onChange={handleChange}
              isMobile={isMobile}
            />
          ) : (
            <ApplicationCollapseHeader
              dependentName={NOT_PROVIDED}
              dependentTaxNumber=""
              expanded={disabled}
              icon={humanMaleFemale}
              taxNumberLabel=""
              handleExpandClick={() => { }}
              isMobile={isMobile}
            />
          )}
          <Box height="36px" />
        </OcCard>
        <Box height="16px" />
        <TaxCreditApplicationButtonBars
          taxApplicationSaveEnabled={taxApplicationSaveEnabled && editable}
          cancelData={handleCancel}
          handlePrint={handlePrint}
          saveData={
            radioGroupState === "alone"
              ? () => setShowConfirmDialog(true)
              : handleSave
          }
          editable={editable}
          disabledButton={disabledButton}
          handleRemoveRequest={handleRemoveRequest ?? (() => { })}
          showModifiedButton={showModifiedButton}
          showRemoveButton={showRemoveButton}
          isModified={false}
          isModifiedDeclaration={false}
          isMobile={isMobile}
        />
      </Box>
      <OcConfirmationDialog
        showDialog={showConfirmDialog}
        onClose={handleDialogConfirm}
        onCancel={handleDialogCancel}
        title={t("OC_CONFIRM_SETTING")}
        texts={[t("MY_TAX_FAMILY_TAX_APPLICATION_INFO")]}
        okButtonText={t("OC_ALL_RIGHT")}
        cancelButtonText={t("OC_CANCEL")}
      />
    </>
  );
};

export default MyTaxCombinedTaxCreditApplication;
